/**
 * Live projection updates over SSE.
 *
 * The stream carries versioned deltas, never data. On a delta the screen
 * refetches its projection, so what is rendered is always a complete,
 * server-derived projection rather than a locally patched approximation.
 *
 * The connection state is surfaced: an operator must be able to see that the
 * screen has stopped updating.
 */
import { useEffect, useRef, useState } from 'react';
import type { LiveChannel } from '@dcc/shared';

export type LiveConnection = 'connecting' | 'live' | 'reconnecting' | 'offline';

export interface LiveState {
  connection: LiveConnection;
  lastEventAt: string | null;
  /** Increments when the server says the cursor gapped and a refetch is due. */
  gapCount: number;
}

/**
 * `enabled` exists for one caller: demo mode, which renders the same screens
 * from a projection built in the browser. A demo that opened a stream would be
 * making a request to the live server, which is exactly the property demo mode
 * promises it does not have.
 */
export function useLive(channels: LiveChannel[], onDelta: () => void, enabled = true): LiveState {
  const [state, setState] = useState<LiveState>({
    connection: 'connecting',
    lastEventAt: null,
    gapCount: 0,
  });
  const handler = useRef(onDelta);
  handler.current = onDelta;
  const channelKey = channels.join(',');

  useEffect(() => {
    if (!enabled) {
      setState((s) => ({ ...s, connection: 'offline' }));
      return;
    }
    if (typeof EventSource === 'undefined') {
      setState((s) => ({ ...s, connection: 'offline' }));
      return;
    }
    const source = new EventSource('/api/v1/stream', { withCredentials: true });
    const wanted = new Set(channelKey.split(','));

    const onOpen = () => setState((s) => ({ ...s, connection: 'live' }));
    const onError = () =>
      setState((s) => ({
        ...s,
        // EventSource retries on its own; say "reconnecting" rather than
        // pretending the data on screen is current.
        connection: s.connection === 'live' ? 'reconnecting' : 'offline',
      }));
    const onGap = () => {
      setState((s) => ({ ...s, gapCount: s.gapCount + 1 }));
      handler.current();
    };

    source.addEventListener('open', onOpen);
    source.addEventListener('error', onError);
    source.addEventListener('gap', onGap);

    const listeners: [string, EventListener][] = [];
    for (const channel of wanted) {
      const listener: EventListener = () => {
        setState((s) => ({ ...s, connection: 'live', lastEventAt: new Date().toISOString() }));
        handler.current();
      };
      source.addEventListener(channel, listener);
      listeners.push([channel, listener]);
    }

    return () => {
      source.removeEventListener('open', onOpen);
      source.removeEventListener('error', onError);
      source.removeEventListener('gap', onGap);
      for (const [channel, listener] of listeners) source.removeEventListener(channel, listener);
      source.close();
    };
  }, [channelKey, enabled]);

  return state;
}
